/** Mark Saba (ATHB) sheet-sync card (Tom 2026-08-13: flip dry-run/live from the
 * app, never Render). The worker reads Mark's published inventory mirror every
 * 30 min; this card shows its last report and owns the live-write switch
 * (rec_org_settings MARK_SHEET_SYNC data.apply — the worker reads the same row).
 * Dry run logs what it WOULD do; live inserts Wholesaler leads with Mark's
 * dispo, overwrites prices (logging drops), and retires pending/sold rows. */
import { useCallback, useEffect, useState } from "react";
import { backendFetch } from "../../lib/backend";

interface MarkReport {
  apply?: boolean;
  parsed?: number;
  new?: number;
  price?: number;
  dead?: number;
  kept?: number;
  errors?: number;
  error?: string;
  /** inventory pass only: coming-soon leads that graduated (2026-09-02) */
  promoted?: number;
}

interface MarkStatus {
  apply: boolean;
  last_run: string | null;
  last_report: MarkReport | null;
  /** the Coming Soon sheet pass (2026-09-02) — same apply switch, own clock */
  coming_soon?: { last_run: string | null; last_report: MarkReport | null };
}

async function getMarkStatus(token: string | null): Promise<MarkStatus> {
  return backendFetch<MarkStatus>(token, "/api/scrapers/mark-sheet/status", { timeoutMs: 20000 });
}

async function setMarkApply(token: string | null, apply: boolean): Promise<{ ok: boolean; apply: boolean }> {
  return backendFetch<{ ok: boolean; apply: boolean }>(token, "/api/scrapers/mark-sheet/apply", {
    method: "POST",
    body: { apply },
    timeoutMs: 20000,
  });
}

function ago(iso: string | null): string {
  if (!iso) return "never";
  const t = Date.parse(iso.endsWith("Z") ? iso : iso + "Z");
  if (!Number.isFinite(t)) return "unknown";
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

export function MarkSheetSync({ token, isAdmin }: { token: string | null; isAdmin: boolean }) {
  const [st, setSt] = useState<MarkStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmLive, setConfirmLive] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setSt(await getMarkStatus(token)); } catch { /* transient */ }
  }, [token]);

  useEffect(() => { if (isAdmin) void load(); }, [isAdmin, load]);

  if (!isAdmin) return null;

  const flip = async (apply: boolean) => {
    if (busy) return;
    setBusy(true); setNote(null); setConfirmLive(false);
    try {
      const r = await setMarkApply(token, apply);
      setSt((s) => (s ? { ...s, apply: r.apply } : s));
      setNote(apply
        ? "Live — the next 30-min cycle writes to the CRM."
        : "Dry run — the worker only logs what it would do.");
    } catch (e) {
      setNote((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Toggle failed");
    } finally {
      setBusy(false);
    }
  };

  const rep = st?.last_report;
  const cs = st?.coming_soon;
  const csRep = cs?.last_report;
  return (
    <div className="zfpurge">
      <div className="zfphd">
        <div>
          <b>Mark Saba (ATHB) sheets → CRM</b>
          <div className="sub">
            Reads Mark&apos;s live inventory every 30 min: new AVAILABLE deals in as Mark&apos;s Leads
            (dispo Mark Saba · (480) 618-0366), price changes logged, pending/sold → Dead Deal.
            His <b>Coming Soon</b> sheet too: rows land as &quot;Mark&apos;s Coming Soon&quot; (the Coming Soon Mark view)
            with a notification each, and graduate to Mark&apos;s Leads when they reach the inventory sheet.
          </div>
        </div>
        <div className="zfpacts">
          {st === null ? (
            <span className="sub dim">Loading…</span>
          ) : st.apply ? (
            <>
              <span className="cmppill done">LIVE</span>
              <button className="btn slim" disabled={busy} onClick={() => void flip(false)}>Switch to dry run</button>
            </>
          ) : confirmLive ? (
            <>
              <span className="sub">Start writing to the CRM?</span>
              <button className="btn slim primary" disabled={busy} onClick={() => void flip(true)}>Yes, go live</button>
              <button className="btn slim" disabled={busy} onClick={() => setConfirmLive(false)}>Cancel</button>
            </>
          ) : (
            <>
              <span className="cmppill open">DRY RUN</span>
              <button className="btn slim primary" disabled={busy} onClick={() => setConfirmLive(true)}>Go live</button>
            </>
          )}
        </div>
      </div>
      {note ? <div className="cmpnote">{note}</div> : null}
      {st ? (
        <div className="sub" style={{ marginTop: 6 }}>
          Last check: {ago(st.last_run)}
          {rep ? (
            rep.error ? (
              <> · <span style={{ color: "var(--red, #b91c1c)" }}>{rep.error}</span></>
            ) : (
              <> · {rep.parsed ?? 0} in sheet · {rep.new ?? 0} new · {rep.price ?? 0} price change{(rep.price ?? 0) === 1 ? "" : "s"} · {rep.dead ?? 0} dead{rep.promoted ? ` · ${rep.promoted} promoted from coming soon` : ""}{rep.apply === false ? " (dry run — nothing written)" : ""}</>
            )
          ) : (
            <> · no report yet — the worker runs every 30 min</>
          )}
        </div>
      ) : null}
      {st ? (
        <div className="sub" style={{ marginTop: 4 }}>
          Coming Soon sheet: {ago(cs?.last_run ?? null)}
          {csRep ? (
            csRep.error ? (
              <> · <span style={{ color: "var(--red, #b91c1c)" }}>{csRep.error}</span></>
            ) : (
              <> · {csRep.parsed ?? 0} in sheet · {csRep.new ?? 0} new · {csRep.dead ?? 0} dead{csRep.apply === false ? " (dry run — nothing written)" : ""}</>
            )
          ) : (
            <> · no report yet</>
          )}
        </div>
      ) : null}
    </div>
  );
}
